import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(null);

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = useCallback(({ message, type = 'success', duration = 3500 }) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, dismiss }) {
  return (
    <>
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div style={{
        position: 'fixed', bottom: 24, right: 24,
        zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: 8,
        pointerEvents: 'none',
      }}>
        {toasts.map(t => <ToastItem key={t.id} toast={t} dismiss={() => dismiss(t.id)} />)}
      </div>
    </>
  );
}

function ToastItem({ toast, dismiss }) {
  const ok = toast.type === 'success';
  const accent = ok ? '#1D9E75' : '#E05C5C';
  return (
    <div
      onClick={dismiss}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 14px',
        borderRadius: 10,
        background: 'var(--bg2)',
        border: '0.5px solid var(--border)',
        borderLeft: `3px solid ${accent}`,
        boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
        animation: 'toast-in 0.22s ease',
        pointerEvents: 'all',
        cursor: 'pointer',
        minWidth: 240, maxWidth: 360,
        userSelect: 'none',
      }}
    >
      <i
        className={`ti ${ok ? 'ti-circle-check' : 'ti-alert-circle'}`}
        style={{ fontSize: 16, color: accent, flexShrink: 0 }}
        aria-hidden="true"
      />
      <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: 'var(--text)', lineHeight: 1.4 }}>
        {toast.message}
      </span>
      <button
        onClick={e => { e.stopPropagation(); dismiss(); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0, fontSize: 16, lineHeight: 1, flexShrink: 0 }}
        aria-label="Dismiss"
      >×</button>
    </div>
  );
}
